import type { Asset, Entry, EntrySkeletonType, UnresolvedLink } from "contentful";

// biome-ignore lint/suspicious/noExplicitAny: Contentful Entry generics require `any` for modifier/locale wildcards
type AnyEntry = Entry<EntrySkeletonType, any, any>;
type AnyAsset = Asset<undefined, string>;
type EntryOf<T> = Extract<T, AnyEntry>;
type EntryLike = AnyEntry | UnresolvedLink<"Entry"> | null | undefined;
type AssetLike = AnyAsset | UnresolvedLink<"Asset"> | null | undefined;

const isRecord = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object";

const hasContentfulFields = (value: unknown): value is { sys: Record<string, unknown> } & Record<string, unknown> => {
  if (!isRecord(value)) return false;

  const sys = value.sys as Record<string, unknown> | undefined;
  return "fields" in value && "metadata" in value && isRecord(sys) && "id" in sys;
};

export function isEntry<T extends EntryLike>(value: T): value is EntryOf<T>;
export function isEntry(value: unknown): value is AnyEntry;
export function isEntry(value: unknown): value is AnyEntry {
  if (!hasContentfulFields(value)) return false;
  const sys = value.sys as Record<string, unknown>;
  return "contentType" in sys;
}

export function getEntry<T extends EntryLike>(value: T): EntryOf<T> | null;
export function getEntry(value: unknown): AnyEntry | null;
export function getEntry(value: unknown): AnyEntry | null {
  return isEntry(value) ? value : null;
}

export function isEntryOfContentType<T extends AnyEntry>(value: unknown, contentTypeId: string): value is T {
  return isEntry(value) && value.sys.contentType.sys.id === contentTypeId;
}

export function getEntryOfContentType<T extends AnyEntry>(value: unknown, contentTypeId: string): T | null {
  return isEntryOfContentType<T>(value, contentTypeId) ? value : null;
}

export function getEntriesOfContentType<T extends AnyEntry>(values: unknown[], contentTypeId: string): T[] {
  return values
    .map(value => getEntryOfContentType<T>(value, contentTypeId))
    .filter((entry): entry is T => entry !== null);
}

export function isAsset(value: AssetLike): value is AnyAsset;
export function isAsset(value: unknown): value is AnyAsset;
export function isAsset(value: unknown): value is AnyAsset {
  if (!hasContentfulFields(value)) return false;
  const sys = value.sys as Record<string, unknown>;
  return sys.type === "Asset";
}

export function getAsset(value: AssetLike): AnyAsset | null;
export function getAsset(value: unknown): AnyAsset | null;
export function getAsset(value: unknown): AnyAsset | null {
  return isAsset(value) ? value : null;
}
